import { createContext, type ReactNode, useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { AuthFrame } from '../auth/AuthFrame';
import { SharedDrawer } from './shared-drawer';

/**
 * Signage lettering, matching `AuthFrame`'s footer exactly.
 *
 * Exported because a till screen's own section headings are set in it — the
 * standing facts on this shell and the "Admission" / "Activities" headings
 * inside it are the same kind of label on the same plate, and two copies of the
 * class string would drift the moment the tracking is adjusted.
 */
export const tillPlate =
  'font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase';

/**
 * A region inside a till screen that scrolls, with the two things a scroll
 * container has to say written down rather than inherited.
 *
 * **`overflow-x-hidden`, because otherwise the spec picks the axis and does not
 * say so.** Per CSS Overflow 3, an `overflow-y` that is not `visible` computes
 * the *other* axis from `visible` to `auto` — so `overflow-y-auto` on its own is
 * a scroll container on both axes, and the horizontal one appears nowhere in the
 * class list to be read. Measured on the goods grid at 1280x800: an item name
 * with no break opportunity gave `scrollWidth` 767 against `clientWidth` 760 and
 * a `scrollLeft` that moved to 7. That is the sideways scroll in issue #203, on
 * an axis nobody chose.
 *
 * **It is `hidden` and not `clip`, and that was measured too.** `clip` is the
 * stricter value and the one this wanted — not a scroll container at all, so
 * nothing can scroll it sideways, not even a browser bringing a focused child's
 * ring into view. It cannot be had here: the same spec computes `clip` to
 * `hidden` when the other axis is `auto`, and Chromium duly reported
 * `overflow-x: hidden` for an `overflow-x-clip` written beside `overflow-y-auto`.
 * `hidden` is therefore what this says, because it is what it gets. It is also
 * what the registry's own `select` writes for the same pairing.
 *
 * **`p-1` for the ring, `-m-1` to give the room back.** The button carries
 * `focus-visible:ring-3`, 3px drawn *outside* its border box, and a scroll
 * container clips at its padding box. The three regions offered `pr-1` and
 * nothing else, so the measured clearance was 4px on the right — which does
 * clear — and **0px on the left, the top and the bottom**, where the ring was
 * clipped away entirely. Padding all four edges is the fix; the matching
 * negative margin is what stops that padding reading as a 4px indent against the
 * heading above, the same trade `back-office/Screen.tsx` makes with `-mx-4 … ps-4`.
 *
 * Shared rather than repeated because the broken pairing was already on three
 * screens, and a fourth is one paste away.
 */
export const tillScroll = 'overflow-x-hidden overflow-y-auto -m-1 p-1';

/**
 * Where a till screen's shift-level chrome is drawn: beside the mark, above the
 * datum.
 *
 * `null` until the frame has mounted its slot, which is one paint and is why
 * {@link TillActions} renders nothing rather than falling back to drawing the
 * controls somewhere else. There is no second place for them.
 */
const TillActionsSlot = createContext<HTMLDivElement | null>(null);

/**
 * The two controls a till screen has that are not about the sale in front of
 * it — *Today's sales*, *End shift*, *Back to the counter* — placed in the
 * frame's upper zone instead of in a band of their own.
 *
 * **This is Move 1 of issue #206.** `Sell goods` plus *Today's sales* plus *End
 * shift* was the back-office title/action band transplanted onto a till, and a
 * cashier knows what screen they are on. Both controls are **shift-level rather
 * than sale-level**, and the zone above the datum is already "who and where",
 * which is what a shift is. Measured at 1000x625: the band and its gap were
 * **76px**, and this header grew from 56px to 88px to take the controls — so the
 * field went 467px to 435px while the working area above the sale went 391px to
 * 435px. **44px back**, and shift chrome stops competing with the sale for the
 * eye.
 *
 * **A portal and not a prop, because the screen that owns the control is a
 * grandchild of the frame.** `Till.tsx` renders `TillFrame` and puts `SellGoods`
 * inside it; `SellGoods` then swaps itself for `EndShift` or `TodaysSales` on
 * its own state. Threading an `actions` node back up through both would mean
 * lifting that state into `Till.tsx` — a much larger change than the one being
 * asked for, and one that would put the counter's view switch a long way from
 * the counter. A portal keeps the buttons where they are declared, with the
 * handlers and the state they close over, and moves only the DOM.
 *
 * The slot is early in the document, so these take their natural place at the
 * top of the tab order: they are chrome, and a cashier tabbing forward reaches
 * the sale rather than leaving it.
 *
 * `label` is the screen's own name, for the two screens that are pushed over the
 * counter and would otherwise be an unlabelled list. The counter screens pass
 * none — that is the whole argument above.
 */
export function TillActions({ label, children }: { label?: string; children?: ReactNode }) {
  const slot = useContext(TillActionsSlot);

  if (slot === null) return null;

  return createPortal(
    <>
      {label !== undefined && <span className={tillPlate}>{label}</span>}
      {children !== undefined && <div className="ms-auto flex items-center gap-3">{children}</div>}
    </>,
    slot,
  );
}

/**
 * The chrome a till screen sits in.
 *
 * **The back office and the till are different navigation shells** — a till
 * screen has no sidebar, it has the sale in front of it and a way back
 * (`docs/ui-conventions.md`, Navigation). So this is not `BackOfficeShell`, and
 * it deliberately reuses the *language* rather than the frame: the datum, the
 * lockup slot and the signage lettering are the same, because a cashier and a
 * manager are looking at one system.
 *
 * What differs is the scale. Everything inside is built to the till column of
 * "The two scales" — few large targets, no scrolling to complete a sale, and
 * `--size-touch` as the floor for anything tapped. That is a decision per screen
 * and not per device: nothing here branches on Electron or on
 * `pointer: coarse`, because an all-in-one POS with a mouse plugged in reports a
 * fine pointer and would silently get the desk density.
 *
 * The footer states who and where, the way the signed-out frame does, so a
 * terminal that has been left on all night says what it is at a glance.
 */
export function TillFrame({
  title,
  outlet,
  switcher,
  shared = false,
  standing,
  version,
  children,
}: {
  /** What this screen is, in the footer's signage lettering. */
  title: string;
  /**
   * The outlet this till is selling for, named on a plate beside the mark.
   *
   * **Issue #243: a machine can sell for more than one outlet, and a sale made
   * at the wrong one is only put right by a refund.** So where it is selling is
   * on every till screen, in the zone above the datum that already means "who
   * and where", large enough to read from the queue side of the counter. It is
   * the garden's own device: an enamelled bed marker, slate with the name
   * lettered on it, naming the bed you are standing at. That is the one bold
   * thing this frame adds, and it is spent where getting it wrong costs money.
   *
   * Absent where the device has never been told a name and holds one
   * registration, which is every till that has never needed telling apart.
   */
  outlet?: string | undefined;
  /**
   * The way to another outlet, drawn beside the plate it changes (#254). Only
   * on the selling screens; the sign-on screen lists the outlets under *Start shift*.
   */
  switcher?: ReactNode;
  /** Whether this device sells for more than one outlet — see `shared-drawer.ts`. */
  shared?: boolean;
  /**
   * The standing fact for the other end of the footer — who is at the till, or
   * which outlet it stands at. A short noun phrase; anything longer belongs in
   * the body of the screen.
   */
  standing?: ReactNode;
  /**
   * The build the running shell reports (#306), after the standing fact and in
   * its lettering — a fact in the margin, so a photograph of any till screen
   * says what the machine is running. Bare: `1.0.1` is read out over a phone as
   * it stands, and a `v` or the word "version" would be noise. `null` or absent
   * draws nothing at all.
   */
  version?: string | null | undefined;
  /**
   * Optional so the frame can be drawn with nothing in it, which is what the
   * beat before the device's own answers arrive looks like — see `Till`.
   */
  children?: ReactNode;
}) {
  /**
   * The slot node, held in state rather than in a ref, because a ref does not
   * re-render and the portal cannot be drawn until the node exists. A callback
   * ref into `useState` is the ordinary way to say "tell me when this mounts".
   */
  const [actions, setActions] = useState<HTMLDivElement | null>(null);

  return (
    // **`h-screen` and not `min-h-screen`, and this frame is where that is
    // enforced rather than hoped for.** "No scrolling to complete a sale" is the
    // till column of `docs/ui-conventions.md`, "The two scales", and it was a
    // rule every screen had to keep for itself — which `Sell goods` did not: at
    // 1280x800 the page ran past the fold and put `Take payment` where a cashier
    // had to scroll for it with a customer waiting.
    //
    // A screen that outgrows the counter is now a screen that has to say where
    // it scrolls *inside* itself, which is a decision somebody makes on purpose
    // rather than a page that quietly gets taller. `min-h-0` on the main is what
    // lets a child do that: without it a flex item refuses to shrink below its
    // content and the overflow moves back out here, where it is clipped instead.
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* **The mark without the wordmark, and only here.** The Electron window
          title already reads *Government Botanical Garden Munnar*; the lockup
          repeated it ~60px below, which on a full-screen kiosk is the same
          string twice in one eyeful. `AuthFrame.Emblem` is that crop, and the
          signed-out frame keeps its whole lockup.

          `min-h-touch` rather than a `py`: the row now holds `size="touch"`
          controls and a header that changes height with them is a field below it
          that changes with it. Stating the floor means the header is the same
          88px whether a screen has chrome in it or not, so nothing under the
          datum moves when a screen is swapped. */}
      <header className="flex min-h-touch shrink-0 items-center gap-4 px-6 py-4 sm:px-10">
        <AuthFrame.Emblem />

        {/* Slate with mist lettering — the ink and the ground, reversed, which is
            the pairing Staffing's checked state already uses; no new colour.
            The signage face at `text-xl` is this screen's one step up in size:
            the counter screens carry no page title, and where they are is the
            title that matters. `max-w` and `truncate` so a long name gives way
            before it pushes *End shift* off the row. */}
        {outlet !== undefined && (
          <p className="max-w-[20rem] min-w-0 shrink truncate rounded-lg bg-foreground px-4 py-2 font-display text-xl leading-tight font-semibold tracking-[0.12em] text-background uppercase">
            <span className="sr-only">Selling for </span>
            <span>{outlet}</span>
          </p>
        )}

        {/* Beside the plate and not on it: the plate is a marker, and the switch
            is a control of its own, the same outline pill as every other. */}
        {switcher}

        {/* The slot itself. `contents` so the flex row above lays the label and
            the controls out directly — a wrapper `div` here would make the two
            one item and put `ms-auto` on the wrong box. */}
        <div ref={setActions} className="contents" />
      </header>

      {/* The datum, as the signed-out frame draws it. The signature is the one
          place boldness is spent, and a till is still the same system. */}
      <div className="h-[3px] w-full shrink-0 datum bg-foreground" />

      <main className="flex min-h-0 flex-1 flex-col px-6 py-6 sm:px-10">
        <SharedDrawer value={shared ? outlet : undefined}>
          <TillActionsSlot value={actions}>{children}</TillActionsSlot>
        </SharedDrawer>
      </main>

      <div className="h-[3px] w-full shrink-0 datum bg-foreground" />

      <footer className="flex shrink-0 items-baseline justify-between gap-4 px-6 py-4 sm:px-10">
        <span className={tillPlate}>{title}</span>
        {(standing !== undefined || version != null) && (
          <span className={tillPlate}>
            {standing}
            {standing !== undefined && version != null && ' · '}
            {version}
          </span>
        )}
      </footer>
    </div>
  );
}
